import React, {useCallback, useState} from 'react';

import {useGate, useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {toast} from 'react-toastify';

import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';

import {Button} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {
    CopyButton,
    Id,
    StatusWrapper,
    Title,
    TitleText,
    TitleWrapper,
} from 'components/ui-kit/PageComponents';
import {Stepper} from 'components/ui-kit/Stepper';
import {DOCUMENT_STATUS_COLORS} from 'core/constants/statuses';
import {DocumentStatus} from 'core/dictionaries';
import {
    DocumentsReturnsGate,
    step$,
    stepChanged,
    submitDocumentReturnGoodsFx,
} from 'models/documents/returns';
import {colors} from 'theme';
import {ReturnGoodsReport} from 'types';
import {useClipboard} from 'utils/custom-hooks';

import {AdditionalInfo} from './AdditionalInfo';
import {GeneralInfo} from './GeneralInfo';
import {HeaderLeft} from './styled';

export const ReturnInfo = ({data}: {data: ReturnGoodsReport}) => {
    useGate(DocumentsReturnsGate);
    const statusUpdatePending = useStore(submitDocumentReturnGoodsFx.pending);
    const {t} = useTranslation();
    const translate = useCallback((key: string) => t(`pages.documents.returnGoods.${key}`), [t]);
    const step = useStore(step$);
    const copy = useClipboard();
    const [storageLocation, setStorageLocation] = useState<number | null>(null);

    const isPending = data?.status === DocumentStatus.PENDING;
    const statusLabel = t(`dictionaries.documents.status.${data?.status}`);

    const copyCode = () => {
        copy(data?.reportId ?? '');
        toast.success(t('pages.documents.details.idCopied'));
    };

    return (
        <>
            <TitleWrapper>
                <HeaderLeft>
                    <TitleText>
                        <Title>
                            {translate('consumerReturn')} <Id>{data?.reportId ?? ''}</Id>
                        </Title>
                        <CopyButton
                            icon={
                                <Icon
                                    source={CopyIcon}
                                    width={15}
                                    height={15}
                                    fillColor={colors.brandPrimary}
                                />
                            }
                            onClick={copyCode}
                            hoverIndication={true}
                            label={t('pages.documents.details.copyId')}
                            labelPlace={'bottom'}
                        />
                    </TitleText>
                    <StatusWrapper color={DOCUMENT_STATUS_COLORS[data?.status ?? 'DEFAULT']}>
                        {statusLabel}
                    </StatusWrapper>
                </HeaderLeft>
                {isPending && (
                    <Button
                        isLoading={statusUpdatePending}
                        type="button"
                        style={{width: 'auto'}}
                        disabled={storageLocation == null}
                        onClick={() => {
                            if (!data?.reportId || storageLocation == null) {
                                return;
                            }

                            submitDocumentReturnGoodsFx({
                                reportId: data.reportId,
                                storageLocation,
                            });
                        }}
                    >
                        {translate('sendDocument')}
                    </Button>
                )}
            </TitleWrapper>

            <Stepper
                steps={[
                    {
                        label: translate('generalInfo'),
                        component: (
                            <GeneralInfo
                                data={data}
                                showStorageLocation={isPending}
                                storageLocation={storageLocation}
                                onStorageLocationChange={setStorageLocation}
                            />
                        ),
                    },
                    {
                        label: translate('additionalInfo'),
                        component: <AdditionalInfo data={data} />,
                    },
                ]}
                activeStep={step}
                isClickable
                clickCallback={stepChanged}
            />
        </>
    );
};
